import { useState } from 'react';
import { Box, Button, Code, InlineStack, Stack, Text, Toggle } from '@customerio/pluma-components/react';
import { useCanSendTestEvent } from '@/services/permissions';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import { customerIdentifierDisplay } from '@/services/db/customer/display';
import { generatePath } from '@/services/router/generate-path';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { segmentPath } from './cells';
import { useOpenSendEventModal, type SendEventForm, type SendEventRecipient } from '@/features/send-event';
import { FormattedLogSource } from './formatted-log-source';
import { InboundMessagePreview } from './inbound-message-preview';
import { LogJsonFormatter, type GeoRefResolver, type SegmentLinkResolver } from './log-json-formatter';
import { LOG_TYPE } from '../activity-log-types';
import { activityLogJson } from '../log-json';
import { useGeoRefResolver } from '../use-geo-refs';
import type { IdentifierConfig } from '../display-name';
import type { ActivityLog, ActivityLogRow, ActivityLogSegment } from '../types';

/**
 * Expanded-row detail: the event data (via `LogJsonFormatter`, with identifier/reserved labels,
 * humanized timestamps, segment links, and channel-formatted reasons), the source description(s),
 * the anonymous id, Merge-profiles and Resend. The event data carries its own copy button.
 *
 * An inbound message shows its message preview first, with a "View JSON data" toggle to switch to
 * the event data; every other type shows the event data alone.
 */

/** Hide action sources for newsletter/transactional (broken data). */
function filteredSources(log: ActivityLog) {
  return (log.sources ?? []).filter((source) => {
    if (source.type !== 'action') return true;
    return !log.name.includes('newsletter') && !log.name.includes('transactional');
  });
}

/** The merged anonymous id, if any. */
function mergedAnonymousId(log: ActivityLog): string | undefined {
  if (log.type === 'anon_merge') return undefined;
  const source = (log.sources ?? []).find((s) => !isEmptyString(s.anonymous_id ?? ''));
  return source?.anonymous_id ?? undefined;
}

/** The `conflicting_people_id` from one identifier-keyed attr value, if it carries one. */
function conflictingIdFrom(value: unknown): string | null {
  if (!isRecord(value)) return null;
  const id = value.conflicting_people_id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
}

/**
 * For a failed attribute change, the first `conflicting_people_id` across the identifier-keyed
 * attrs — the profile the Merge action would resolve against.
 */
function conflictingCustomerId(log: ActivityLog): string | null {
  if (log.type !== 'failed_attribute_change') return null;
  if (!isRecord(log.attrs)) return null;
  for (const value of Object.values(log.attrs)) {
    const id = conflictingIdFrom(value);
    if (id != null) return id;
  }
  return null;
}

/** Merge-profiles link, pre-filling the conflicting ids — or undefined when Merge doesn't apply. */
function buildMergeHref(log: ActivityLog, workspaceId: string, conflictId: string | null): string | undefined {
  const search = { primary_id: log.customer_id ?? undefined, secondary_id: conflictId ?? undefined };

  if (conflictId == null || isEmptyString(log.customer_id ?? '')) return undefined;

  return generatePath('/workspaces/:workspaceId/people/merge', { workspaceId }, search);
}

/**
 * Who a resend goes to. A feed scoped to one person knows who they are, so a resend does not wait
 * on the response sideloading them (Ember resends to the page's own customer on an individual's
 * activity).
 */
function resendCioId(recipient: SendEventRecipient | undefined, customer: ActivityLogRow['customer']): string {
  return recipient?.cioId ?? customer?.internal_id ?? '';
}

/** An Event log for a known profile can be re-sent. */
function canResendLog(log: ActivityLog, isAnonymous: boolean, cioId: string): boolean {
  return !isAnonymous && log.type === 'event' && !isEmptyString(log.customer_id ?? '') && !isEmptyString(cioId);
}

/** All the payload-derived, hook-free state the expanded row needs (kept pure for readability). */
function deriveExpandedRowState(
  row: ActivityLogRow,
  isAnonymous: boolean,
  mergeHref: string | undefined,
  recipient: SendEventRecipient | undefined,
) {
  const { log, customer } = row;
  const copyable = activityLogJson(log);
  const cioId = resendCioId(recipient, customer);
  return {
    sources: filteredSources(log),
    anonId: isAnonymous ? undefined : mergedAnonymousId(log),
    // Displayed event data is the raw `attrs`.
    jsonString: log.attrs == null ? null : JSON.stringify(log.attrs, null, 2),
    // The copy button is gated on the normalized json value (undefined for many types → no button),
    // and copies that value, not the raw attrs.
    copyable,
    showCopyJson: copyable !== undefined,
    cioId,
    showMerge: mergeHref != null,
    showResend: canResendLog(log, isAnonymous, cioId),
  };
}

/** The "View JSON data" toggle an inbound message offers, off until turned on. */
type JsonToggleState = { isOn: boolean; onChange: (isOn: boolean) => void };

type EventDataBlockProps = {
  log: ActivityLog;
  jsonString: string | null;
  identifiers: IdentifierConfig;
  resolveSegmentLink: SegmentLinkResolver;
  resolveGeoRef: GeoRefResolver | undefined;
  /** The log's normalized JSON, which the copy button copies; none for types without one. */
  copyCode: string | undefined;
};

function EventDataBlock({
  log,
  jsonString,
  copyCode,
  identifiers,
  resolveSegmentLink,
  resolveGeoRef,
}: EventDataBlockProps) {
  if (jsonString == null) {
    return (
      <Text size="sm" color="secondary">
        No event data
      </Text>
    );
  }
  return (
    // `fs-mask`: event attributes can contain customer PII — redact the whole block from FullStory
    // session replay (masking the full event data is the safe superset).
    <Box className="fs-mask">
      <LogJsonFormatter
        data={log.attrs}
        logType={log.type}
        identifiers={identifiers}
        showNestedIdentifiers={log.type === 'profile_merge'}
        resolveSegmentLink={resolveSegmentLink}
        resolveGeoRef={resolveGeoRef}
        formatErrorType={log.delivery_type}
        code={copyCode}
        withCopyButton={copyCode != null}
      />
    </Box>
  );
}

type SourcesBlockProps = {
  sources: ReturnType<typeof filteredSources>;
  log: ActivityLog;
  workspaceId: string;
  campaignSingular: string;
};

function SourcesBlock({ sources, log, workspaceId, campaignSingular }: SourcesBlockProps) {
  if (sources.length === 0) return null;
  return (
    <Box>
      <Text size="sm" color="secondary" weight="bold" as="span">
        {sources.length === 1 ? 'Source: ' : 'Sources:'}
      </Text>
      {sources.length === 1 ? (
        <Text size="sm" color="secondary" as="span">
          <FormattedLogSource
            log={log}
            source={sources[0]}
            workspaceId={workspaceId}
            campaignSingular={campaignSingular}
          />
        </Text>
      ) : (
        <Stack gap="050">
          {sources.map((source, index) => (
            <Text key={index} size="sm" color="secondary" as="span">
              <FormattedLogSource
                log={log}
                source={source}
                workspaceId={workspaceId}
                campaignSingular={campaignSingular}
              />
            </Text>
          ))}
        </Stack>
      )}
    </Box>
  );
}

type MergedFromBlockProps = { fromCustomerInternal: string | null | undefined };

/** The profile a merge absorbed (Ember `details`: "Merged-from Profile ID"). */
function MergedFromBlock({ fromCustomerInternal }: MergedFromBlockProps) {
  if (isEmptyString(fromCustomerInternal)) return null;
  return (
    <Box>
      <Text size="sm" color="secondary" weight="bold" as="span">
        Merged-from Profile ID:
      </Text>
      <Text size="sm" color="secondary" as="span">
        {' '}
        {fromCustomerInternal}
      </Text>
    </Box>
  );
}

type AnonymousIdBlockProps = { anonId: string | undefined };

function AnonymousIdBlock({ anonId }: AnonymousIdBlockProps) {
  if (anonId == null) return null;
  return (
    <Box>
      <Text size="sm" color="secondary" weight="bold" as="span">
        anonymous_id
      </Text>
      <Text size="sm" color="secondary" as="span">
        {': '}
        {/* The anonymous id is an end-user pseudonymous identifier — keep it out of FullStory. */}
        <Code size="xxs" className="fs-mask">
          {anonId}
        </Code>
      </Text>
    </Box>
  );
}

type RowActionsProps = {
  jsonToggle: JsonToggleState | null;
  showMerge: boolean;
  mergeHref: string | undefined;
  showResend: boolean;
  /** Whether the user may actually resend. */
  canResend: boolean;
  onResend: () => void;
};

function RowActions({ jsonToggle, showMerge, mergeHref, showResend, canResend, onResend }: RowActionsProps) {
  if (!showMerge && !showResend && jsonToggle == null) return null;
  return (
    // `flexShrink="0"` is Ember's, carried over as it stands
    // (`activity-log-row/details/index.gts:132`): it did the work when these
    // buttons were a column of their own, and says nothing in a stack.
    <InlineStack flexShrink="0" gap="200" alignInline="flex-end">
      {showMerge && mergeHref != null ? (
        <Button variant="secondary" size="sm" href={mergeHref}>
          Merge profiles…
        </Button>
      ) : null}
      {showResend ? (
        // Show Resend for any eligible log, but disable (with the role-restriction tooltip) when the
        // user lacks send permission — rather than opening a dead read-only modal.
        <ViewerRestrictedTooltip readOnly={!canResend}>
          {(context, effectiveReadOnly) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              variant="secondary"
              size="sm"
              isDisabled={effectiveReadOnly}
              onClick={onResend}
            >
              Resend
            </Button>
          )}
        </ViewerRestrictedTooltip>
      ) : null}
      {jsonToggle != null ? (
        <Toggle
          label="View JSON data"
          size="sm"
          isChecked={jsonToggle.isOn}
          onChange={(event) => jsonToggle.onChange(event.currentTarget.checked)}
        />
      ) : null}
    </InlineStack>
  );
}

type ExpandedRowProps = {
  row: ActivityLogRow;
  workspaceId: string;
  /**
   * The resolved numeric id. `workspaceId` may be the `last` alias, which a
   * link can carry but a request cannot: `Number('last')` is NaN, so the send
   * would address workspace "NaN" and the recipient search would never run.
   */
  dataWorkspaceId: string;
  campaignSingular: string;
  isAnonymous: boolean;
  /** Workspace identifier config, for the JSON formatter's "Identifier" badges. */
  identifiers: IdentifierConfig;
  /** Sideloaded segments (by id), for resolving `entered`/`exited` arrays to name links. */
  segmentById: Map<number, ActivityLogSegment>;
  /** Refetch the log list after a successful resend. */
  onResent?: () => void;
  /**
   * What a resend from this surface reports, and what it opens with.
   *
   * Three surfaces render this row and the funnels count them apart, so the
   * source is the caller's to say. `eventName` is the fallback for a log that
   * carries none: an item's own feed prefills the item. Omit it on a surface
   * that offers no resend, as Ember's rows without `onSendTestWithLog` do.
   * `form` resends a form submission as a test submission of that form.
   */
  resend?: { source: string; eventName?: string; form?: SendEventForm; recipient?: SendEventRecipient };
};

export function ExpandedRow({
  row,
  workspaceId,
  dataWorkspaceId,
  campaignSingular,
  isAnonymous,
  identifiers,
  segmentById,
  onResent,
  resend,
}: ExpandedRowProps) {
  const { log, customer } = row;
  const openSendEvent = useOpenSendEventModal();
  const [isJsonOn, setIsJsonOn] = useState(false);
  const hasJsonToggle = log.type === LOG_TYPE.InboundMessage;
  const canResend = useCanSendTestEvent();

  const mergeHref = buildMergeHref(log, workspaceId, conflictingCustomerId(log));

  const { sources, anonId, jsonString, copyable, showCopyJson, cioId, showMerge, showResend } = deriveExpandedRowState(
    row,
    isAnonymous,
    mergeHref,
    resend?.recipient,
  );

  // Resolve the geoset/geofence id attrs on a geofence transition event to name links; only
  // fetches for geofence logs, once the row is expanded.
  const resolveGeoRef = useGeoRefResolver(log, workspaceId);

  // Resolve segment ids in `entered`/`exited` arrays to name links from the sideloaded segments
  //; a missing id renders as "Deleted segment (id)".
  const resolveSegmentLink: SegmentLinkResolver = (id) => {
    const segment = segmentById.get(id);
    return segment == null ? null : { name: segment.name, href: segmentPath(workspaceId, segment.id) };
  };

  const openResend = () => {
    if (resend == null) return;
    openSendEvent({
      workspaceId: Number(dataWorkspaceId),
      recipient: resend.recipient ?? {
        cioId,
        label: customer == null ? (log.customer_id ?? cioId) : customerIdentifierDisplay(customer, 'Person'),
      },
      eventName: isEmptyString(log.name) ? (resend.eventName ?? '') : log.name,
      eventData: jsonString ?? '{}',
      form: resend.form,
      source: resend.source,
      onSent: onResent,
    });
  };

  // One column, with the actions above the data rather than beside it. Put
  // them in a second column and the JSON and the source line are squeezed
  // into what is left, which is not what the page this replaces does: its
  // row is a single stack, the buttons right-aligned at the top of it and
  // everything below them the full width
  // (`activity-log-row/details/index.gts:128-160`). The spacing is the
  // stack's one gap, so no block inside carries padding of its own — and
  // none on the outside either: the cell this is rendered into brings its
  // own, which is all Ember's row relies on.
  return (
    <Stack gap="100">
      <RowActions
        jsonToggle={hasJsonToggle ? { isOn: isJsonOn, onChange: setIsJsonOn } : null}
        showMerge={showMerge}
        mergeHref={mergeHref}
        showResend={showResend && resend != null}
        canResend={canResend}
        onResend={openResend}
      />

      {hasJsonToggle && !isJsonOn ? (
        <InboundMessagePreview log={log} workspaceId={Number(dataWorkspaceId)} />
      ) : (
        <EventDataBlock
          log={log}
          jsonString={jsonString}
          copyCode={showCopyJson ? JSON.stringify(copyable, null, 2) : undefined}
          identifiers={identifiers}
          resolveSegmentLink={resolveSegmentLink}
          resolveGeoRef={resolveGeoRef}
        />
      )}

      <MergedFromBlock fromCustomerInternal={log.from_customer_internal} />
      <SourcesBlock sources={sources} log={log} workspaceId={workspaceId} campaignSingular={campaignSingular} />
      <AnonymousIdBlock anonId={anonId} />
    </Stack>
  );
}
